import { duration } from '@play-co/components';
import { MutableState } from '../State';
import type { AsyncActionAPI, SyncActionAPI } from '../utils/types';
import {
  getOngoingWeeklyLeaderboardEvent,
  getWeeklyLeaderboardRankingArgs,
  isWeeklyLeaderboardsAvailable,
} from './weeklyLeaderboard.getters';
import {
  weeklyLeaderboardExpirationTime,
  weeklyLeaderboardSchedule,
  weeklyLeaderboardStarScoreMap,
} from './weeklyLeaderboard.ruleset';
import { getOptimizedJitterDelayInMs } from '../chatbot/chatbot.util';

/**
 * Ensure the weekly leaderboard state is initialized and return it
 * @param state
 * @returns
 */
function ensureWeeklyLeaderboardState(state: MutableState) {
  if (!state.weeklyLeaderboard) {
    state.weeklyLeaderboard = {
      entries: {},
    };
  }

  return state.weeklyLeaderboard;
}

/**
 * Join the weekly leaderboard for the current event
 * @param state User state
 * @param now Current time
 */
export function joinWeeklyLeaderboard(state: MutableState, now: number): void {
  // Cannot join if there is no event
  const event = getOngoingWeeklyLeaderboardEvent(state, now);
  if (!event) return;

  const weeklyLeaderboard = ensureWeeklyLeaderboardState(state);

  if (weeklyLeaderboard.entries[event.id]) {
    return;
  }

  // Join the event - opponents will be filled by the matchmaking process
  weeklyLeaderboard.entries[event.id] = {
    score: 0,
    joinedAt: now,
    opponents: [],
  };
}

/**
 * Increase user's weekly leaderboard score for current event
 * @param state User state
 * @param score Score to increase
 * @param now Current time
 */
export function increaseWeeklyLeaderboardScore(
  state: MutableState,
  score: number,
  now: number,
): void {
  // Cannot increase score if there is no event
  const event = getOngoingWeeklyLeaderboardEvent(state, now);
  if (!event) return;

  // Auto-join the leaderboard on score if user has not joined yet
  joinWeeklyLeaderboard(state, now);

  // Cannot increase score if user has never joined a leaderboard
  const entry = state.weeklyLeaderboard?.entries[event.id];
  if (!entry) return;

  // Increase the score
  entry.score += score;
}

/**
 * Increase user score based on puzzle stars
 * @param state
 * @param stars
 * @param api
 */
export function weeklyLeaderboardPuzzleScore(
  state: MutableState,
  stars: number,
  now: number,
): void {
  const score = weeklyLeaderboardStarScoreMap[stars];
  if (!score) return;

  // Increase user score for puzzle stars
  increaseWeeklyLeaderboardScore(state, score, now);
}

/**
 *
 * @param state
 * @param timestamp
 * @returns
 */
export function updateWeeklyLeaderboardLastSeen(
  state: MutableState,
  now: number,
): void {
  // Cannot update if not available
  if (!isWeeklyLeaderboardsAvailable(state, now)) return;

  // Initialize the state if it doesn't exist
  const weeklyLeaderboard = ensureWeeklyLeaderboardState(state);

  // Update the last seen timestamp
  weeklyLeaderboard.lastSeenAt = now;
}

/**
 * Update the position in user's state, so that can be used for analytics
 * @param state
 * @param api
 * @returns
 */
export async function updateWeeklyLeaderboardPlayerRank(
  state: MutableState,
  api: Pick<AsyncActionAPI, 'asyncGetters' | 'date'>,
): Promise<void> {
  const event = getOngoingWeeklyLeaderboardEvent(state, api.date.now());
  if (!event) return;

  const playerEntry = state.weeklyLeaderboard?.entries[event.id];
  if (!playerEntry) return;

  const rankingArgs = getWeeklyLeaderboardRankingArgs(state, event.id);
  if (!rankingArgs) return;

  const ranking =
    await api.asyncGetters.getWeeklyLeaderboardRanking(rankingArgs);

  // Save the user's position in their state
  playerEntry.position =
    ranking.findIndex((entry) => entry.id === state.id) + 1;
}

/*
 * Schedule weekly leaderboard push notifications
 * @param state User state
 * @param api API
 */
export function scheduleWeeklyLeaderboardMessages(
  state: MutableState,
  api: {
    scheduledActions: SyncActionAPI['scheduledActions'];
    date: SyncActionAPI['date'];
    math: SyncActionAPI['math'];
  },
): void {
  // Feature not enabled, do nothing.
  const now = api.date.now();
  if (!isWeeklyLeaderboardsAvailable(state, now)) return;

  // Schedule weekly leaderboard messages for 4h and 28h before the event ends. Should be in descending order.
  const pushTimes = [28, 4] as const;

  // Get the current event end time.
  let eventEnd = weeklyLeaderboardSchedule.getCurrentEvent(now)?.end;

  // Look for the next event if the current one is almost over.
  if (!eventEnd || eventEnd - now < duration({ hours: pushTimes.at(-1) })) {
    eventEnd = weeklyLeaderboardSchedule.getNextEvent(now)?.end;
  }

  // If there is no event, bail out.
  if (!eventEnd) return;

  // Go through the push times and schedule the first notification that is in the future.
  for (const type of pushTimes) {
    const notificationTime = eventEnd - duration({ hours: type });
    const delay = notificationTime - now;
    const jitterMaxDuration =
      type === 4 ? duration({ minutes: 30 }) : duration({ hours: 3 });

    // Generate optimized jitter delay.
    const jitterDelay = getOptimizedJitterDelayInMs(
      api.math.random,
      jitterMaxDuration,
    );

    // Skip if the delay is negative, notification was already sent.
    if (delay <= 0) continue;

    // Schedule the notification.
    api.scheduledActions.schedule.sendWeeklyLeaderboardMessage({
      delayInMS: delay + jitterDelay,
      args: {
        type: `${type}h`,
      },
      notificationId: `weeklyLeaderboard${type}h`,
    });

    // Only schedule one, next one will be scheduled on the next iteration.
    break;
  }
}

/**
 * Cleanup old weekly leaderboard entries
 * @param state
 * @param now
 * @returns
 */
export function cleanupOldWeeklyLeaderboardEntries(
  state: MutableState,
  now: number,
): void {
  const entries = state.weeklyLeaderboard?.entries;
  if (!entries) return;

  // Find old entries based on expiration time.
  const oldEntries = Object.keys(entries).filter((id) => {
    const entry = entries[id];
    return now > entry.joinedAt + weeklyLeaderboardExpirationTime;
  });

  // Delete all old entries
  oldEntries.forEach((id) => delete entries[id]);
}
